import { twMerge } from '@shared/themes';
import { cva } from 'class-variance-authority';
import type { ComponentProps, ReactNode } from 'react';
import type { LotteryName } from '../../lottery-purchases/types';
import { ViewArchive } from '../../lottery-results/components/ViewArchive/ViewArchive';

type LotteryLayoutProps = ComponentProps<'div'> & {
  isHeader?: boolean;
  isLiveDrawView?: boolean;
  showArchivePanel?: boolean;
  lotteryName?: LotteryName;
  activeContent?: ReactNode;
  sidePanelContent?: ReactNode;
};

function resolveSidePanel({
  isLiveDrawView,
  activeContent,
  sidePanelContent,
  viewArchive,
}: {
  isLiveDrawView: boolean;
  activeContent?: ReactNode;
  sidePanelContent?: ReactNode;
  viewArchive: ReactNode;
}) {
  if (isLiveDrawView) return activeContent;
  if (!sidePanelContent) return viewArchive;
  if (!viewArchive) return sidePanelContent;

  return (
    <>
      {sidePanelContent}
      {viewArchive}
    </>
  );
}

/**
 * LotteryLayout component provides a responsive layout for displaying lottery results,
 * supporting an optional side panel (e.g., for next draw information) and main content area.
 *
 * The layout adapts for larger screens, showing the side panel and main content in a grid,
 * and hides the side panel on smaller screens. Custom class names and additional props
 * are supported for flexibility.
 *
 * When `showArchivePanel` is set with `lotteryName`, {@link ViewArchive} is rendered.
 * When `isLiveDrawView` is also set, `activeContent` moves to the side panel and View Archive
 * sits under the date picker on xl+, left-aligned on one line.
 * On smaller screens the archive stays below the live-draw card.
 *
 * @param className - Optional additional class names for the container.
 * @param sidePanelContent - Optional content to display in the side panel.
 * @param showArchivePanel - When true, renders {@link ViewArchive}. Independent of `sidePanelContent`.
 * @param activeContent - Header view content (lottery numbers or live draw).
 * @param children - Main content to display in the body.
 * @returns The rendered ResultsBody component.
 */
function LotteryLayout({
  className,
  children,
  sidePanelContent,
  activeContent,
  lotteryName,
  isHeader = false,
  isLiveDrawView = false,
  showArchivePanel = false,
  ...props
}: LotteryLayoutProps) {
  const viewArchive = showArchivePanel && lotteryName ? <ViewArchive lotteryName={lotteryName} {...(isLiveDrawView ? { align: 'start' } : {})} /> : null;
  const resolvedSidePanel = resolveSidePanel({ isLiveDrawView, activeContent, sidePanelContent, viewArchive });
  const hasSidePanel = !!resolvedSidePanel;

  const innerBody = cva('ll-container-inner grid grid-cols-1', {
    variants: {
      hasSidePanel: {
        /**
         * The side-panel column is sized by its content — the live-draw card is a fixed 528px — so
         * the main column is what absorbs the squeeze, hence `minmax(0,640px)` rather than a flat
         * `640px`.
         *
         * `ll-container-inner` is `min(1280px, viewport - 160px)` from `lg` up, while two columns
         * at their design widths need 640 + 112 + 528 = 1280 exactly. A fixed first column
         * therefore overflowed the container everywhere between `xl` (1280px) and 1440px, where
         * `LotteryHeaderContainer`'s `overflow-hidden` clipped the card. Capped rather than free so
         * the column still stops at its design width from 1440px up.
         */
        true: 'xl:grid-cols-[minmax(0,640px)_1fr] xl:gap-x-28',
        false: '',
      },
      isHeader: {
        true: '',
        false: 'gap-y-8',
      },
      isLiveDrawView: {
        true: '',
        false: '',
      },
    },
    compoundVariants: [
      {
        isHeader: true,
        isLiveDrawView: false,
        class: 'gap-y-4',
      },
      {
        isHeader: true,
        isLiveDrawView: true,
        class: 'gap-y-6 xl:items-start',
      },
    ],
  });

  const sidePanel = cva('', {
    variants: {
      hasSidePanel: {
        true: 'xl:col-2 xl:span-1',
        false: 'hidden',
      },
      isHeader: {
        true: '',
        false: '',
      },
      isLiveDrawView: {
        true: 'order-2 xl:col-start-2 xl:row-start-1 xl:justify-self-end xl:self-start',
        false: '',
      },
    },
    compoundVariants: [
      {
        hasSidePanel: true,
        isHeader: true,
        isLiveDrawView: false,
        class: 'row-2 justify-self-center xl:row-1 xl:justify-self-end xl:self-center',
      },
      {
        hasSidePanel: true,
        isHeader: false,
        isLiveDrawView: false,
        class: 'xl:self-start',
      },
    ],
  });

  const mainContent = cva('flex flex-col', {
    variants: {
      isHeader: {
        true: 'w-full',
        false: 'gap-y-10',
      },
      hasSidePanel: {
        true: 'xl:row-1',
        false: '',
      },
      isLiveDrawView: {
        true: 'order-1 xl:order-none',
        false: '',
      },
    },
    compoundVariants: [
      {
        isHeader: true,
        isLiveDrawView: false,
        class: 'gap-y-4',
      },
      {
        isHeader: true,
        isLiveDrawView: true,
        class: 'gap-y-6',
      },
    ],
  });

  const mainColumn = cva('', {
    variants: {
      isLiveDrawView: {
        true: 'contents xl:flex xl:flex-col xl:gap-6 xl:col-start-1 xl:row-start-1',
        false: 'hidden',
      },
    },
  });

  const below = cva('', {
    variants: {
      isLiveDrawView: {
        true: 'order-3 justify-self-center xl:order-none xl:justify-self-start',
        false: 'hidden',
      },
    },
  });

  const classes = {
    body: twMerge('ll-grid-container', className),
    innerBody: innerBody({ hasSidePanel, isHeader, isLiveDrawView }),
    sidePanel: sidePanel({ hasSidePanel, isHeader, isLiveDrawView }),
    mainColumn: mainColumn({ isLiveDrawView }),
    mainContent: mainContent({ isHeader, hasSidePanel, isLiveDrawView }),
    below: below({ isLiveDrawView }),
  };

  return (
    <div className={classes.body} {...props}>
      <div className={classes.innerBody}>
        {isLiveDrawView ? (
          <>
            <div className={classes.mainColumn}>
              <div className={classes.mainContent}>{children}</div>
              <div className={classes.below}>{viewArchive}</div>
            </div>
            <div className={classes.sidePanel}>{resolvedSidePanel}</div>
          </>
        ) : (
          <>
            <div className={classes.sidePanel}>{resolvedSidePanel}</div>
            <div className={classes.mainContent}>
              {children}
              {activeContent}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export { LotteryLayout };
export type { LotteryLayoutProps };
